import { Button, Modal, ModalProps, Select, notification, Checkbox } from 'antd';
import { ActionDataItem } from 'core/visitRequestReview/types';
import { ShiftGroup } from 'core/visitRequestReview/scheduleShifts';
import React, { useEffect, useMemo, useState } from 'react';

import './scheduleModal.scss';
import { ChevronDownIcon, ChevronUpIcon, SearchIcon } from 'components/Elements/CustomSVGIcon';
import moment, { Moment } from 'moment';
import TextInput from 'components/Elements/TextInput';

// One officer to place on a shift (a system officer or a free-text name).
export interface ShiftOfficerAssignment {
    securityOfficerId: number | null;
    tempSecurityName: string;
}

// The full desired officer set for a shift, identified by its base date row id.
// The backend reconciles the shift's rows to exactly this set — creating a row
// per extra officer and deleting rows for officers removed.
export interface ScheduleShiftPayload {
    dateId: number;
    officers: ShiftOfficerAssignment[];
}

export interface ScheduleCompletePayload {
    shifts: ScheduleShiftPayload[];
}

interface ScheduleModalProps extends ModalProps {
    securityTeam: ActionDataItem[] | null | undefined;
    isModalOpen: boolean;
    // One entry per logical shift (officer copies grouped together).
    shiftData: ShiftGroup[] | null | undefined;
    handleCancel: () => void;
    handleMultiShiftComplete: (payload: ScheduleCompletePayload) => void;
    loading: boolean;
    // Retail sites name their officers by hand — the saved-officer list is not
    // relevant there, so the dropdown is hidden and only free text is offered.
    isRetail?: boolean;
    // Which request is being scheduled. Shown under the title when opened from
    // a list of many requests, where the row clicked is no longer in view.
    requestId?: number | null;
    siteName?: string | null;
}

interface DropdownOption {
    value: string | number;
    label: string;
}

const formatTime = (time: string | Moment) => {
    if (typeof time === 'string') {
        return moment(time, 'HH:mm:ss').format('HH:mm');
    }
    return time.format('HH:mm');
};

const formatShiftLabel = (group: ShiftGroup) => {
    const date = moment(group.date).format('dddd Do MMMM');
    const startTime = formatTime(group.startTime);
    const endTime = formatTime(group.endTime);
    const base = `${date} ${startTime} - ${endTime}`;

    // Multi-officer shifts show how many slots are already filled.
    if (group.officerCount > 1) {
        return `${base} (${group.assignedOfficerNames.length}/${group.officerCount} officers)`;
    }
    return base;
};

const ScheduleModal: React.FC<ScheduleModalProps> = ({
    securityTeam,
    isModalOpen = true,
    handleCancel,
    handleMultiShiftComplete,
    loading,
    shiftData,
    isRetail = false,
    requestId = null,
    siteName = null,
    ...rest
}) => {
    const [selectedSecurityIds, setSelectedSecurityIds] = useState<(string | number)[]>([]);
    const [selectedShiftKeys, setSelectedShiftKeys] = useState<string[]>([]);
    const [shiftDropdownOpen, setShiftDropdownOpen] = useState<boolean>(false);
    const [tempSecurityName, setTempSecurityName] = useState<string>('');
    // Whether the admin has chosen officers themselves — their choice outranks the
    // pre-fill below and survives any later change of shifts.
    const [officersEdited, setOfficersEdited] = useState<boolean>(false);

    // Start each time the modal opens with a clean slate rather than keeping the
    // previous shift/officer selections.
    useEffect(() => {
        if (isModalOpen) {
            setSelectedShiftKeys([]);
            setSelectedSecurityIds([]);
            setTempSecurityName('');
            setOfficersEdited(false);
        }
    }, [isModalOpen]);

    // Only today and future shifts are schedulable.
    const visibleShiftData = useMemo(
        () => shiftData?.filter((item) => !moment(item.date).isBefore(moment(), 'day')) || [],
        [shiftData],
    );

    const shiftDataOptions: DropdownOption[] = useMemo(
        () => visibleShiftData.map((item) => ({ value: item.key, label: formatShiftLabel(item) })),
        [visibleShiftData],
    );

    const handleShiftSelectionChange = (checkedValues: (string | number)[]) => {
        setSelectedShiftKeys(checkedValues.map(String));
    };

    const handleSelectAllShifts = (checked: boolean) => {
        setSelectedShiftKeys(checked ? visibleShiftData.map((shift) => shift.key) : []);
    };

    const isAllSelected = useMemo(
        () => visibleShiftData.length > 0 && selectedShiftKeys.length === visibleShiftData.length,
        [selectedShiftKeys, visibleShiftData],
    );

    const isIndeterminate = useMemo(
        () => selectedShiftKeys.length > 0 && selectedShiftKeys.length < visibleShiftData.length,
        [selectedShiftKeys, visibleShiftData],
    );

    // How many officers may be assigned to the selected shift(s). This is fixed
    // at the date step (officerCount = number of slots per shift), so a
    // single-officer request can only ever have one officer — take the smallest
    // slot count across the selected shifts so we never over-assign.
    const maxOfficers = useMemo(() => {
        const selectedGroups = visibleShiftData.filter((g) => selectedShiftKeys.includes(g.key));
        const groups = selectedGroups.length > 0 ? selectedGroups : visibleShiftData;
        if (groups.length === 0) {
            return 1;
        }
        // officerCount is the shift's slot count (copies.length from
        // groupShiftsByShift, always >= 1). Clamp to a floor of 1 so a malformed
        // 0/null/negative can never disable assignment entirely — every
        // schedulable shift must allow at least one officer.
        return groups.reduce((min, g) => Math.min(min, Math.max(1, g.officerCount || 1)), Infinity);
    }, [visibleShiftData, selectedShiftKeys]);

    // Slots still free for the officer dropdown (a free-text name uses one slot).
    const dropdownOfficerLimit = Math.max(0, maxOfficers - (tempSecurityName ? 1 : 0));

    // Until the admin picks officers themselves, selecting exactly one shift
    // pre-fills its currently-assigned officers so they can add/remove against the
    // real set; multi-select starts empty so a chosen set is applied to all the
    // selected shifts. Once they have chosen, that set is kept across further shift
    // changes and only trimmed to the slots the new selection allows.
    // At a retail site there is no dropdown to pre-fill, so ids always stay empty
    // — nothing hidden from the admin can end up in the submitted officer set.
    useEffect(() => {
        if (officersEdited) {
            setSelectedSecurityIds((ids) => ids.slice(0, dropdownOfficerLimit));
            return;
        }
        if (selectedShiftKeys.length === 1 && !isRetail) {
            const group = visibleShiftData.find((g) => g.key === selectedShiftKeys[0]);
            setSelectedSecurityIds(group ? group.assignedOfficerIds.slice(0, dropdownOfficerLimit) : []);
        } else {
            setSelectedSecurityIds([]);
        }
        // Only react to shift-selection changes, not to the officer edits below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedShiftKeys]);

    const handleSelectSecurity = (values: (string | number)[]) => {
        setSelectedSecurityIds(values);
        setOfficersEdited(true);
    };

    const handleTempSecurityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTempSecurityName(e.target.value);
        setOfficersEdited(true);
    };

    const handleSubmit = () => {
        if (selectedShiftKeys.length === 0) {
            notification.warning({
                message: 'Missing Fields',
                description: 'Please select at least one shift.',
            });
            return;
        }

        // The full desired officer set — system officers plus one optional
        // free-text name. Officer ids are normalised to numbers (antd can hand
        // back either) so the backend always receives number | null. An empty set
        // clears the shift back to Pending.
        const officers: ShiftOfficerAssignment[] = [
            ...selectedSecurityIds
                .map((sid) => Number(sid))
                .filter((sid) => !Number.isNaN(sid))
                .map((sid) => ({ securityOfficerId: sid, tempSecurityName: '' })),
            ...(tempSecurityName ? [{ securityOfficerId: null, tempSecurityName }] : []),
        // Never send more officers than the request's fixed slot count.
        ].slice(0, maxOfficers);

        const selectedGroups = selectedShiftKeys
            .map((key) => visibleShiftData.find((g) => g.key === key))
            .filter((group): group is ShiftGroup => group !== undefined && group.primaryId !== undefined);

        // No officer chosen is only meaningful as a removal: a shift with
        // nobody on it yet cannot be "scheduled" with nobody.
        const removesSomeone = selectedGroups.some((group) => group.assignedOfficerNames.length > 0);
        if (officers.length === 0 && !removesSomeone) {
            notification.warning({
                message: 'Missing Fields',
                description: 'Please select or enter a security officer.',
            });
            return;
        }

        const shifts: ScheduleShiftPayload[] = selectedGroups
            .map((group) => ({ dateId: group.primaryId as number, officers }));

        if (shifts.length === 0) {
            return;
        }

        handleMultiShiftComplete({ shifts });
    };

    const renderShiftDropdown = () => (
        <div className="shift-dropdown">
            <Select
                mode="multiple"
                placeholder="Select Shifts"
                popupClassName="schedule-shift-dropdown"
                style={{ width: '100%', minHeight: '50px', fontSize: '18px' }}
                value={selectedShiftKeys}
                onChange={handleShiftSelectionChange}
                // Options carry plain-text labels so both the search and the
                // selected tags read the shift; the checkbox row is drawn by
                // optionRender.
                options={shiftDataOptions}
                optionFilterProp="label"
                // eslint-disable-next-line react/no-unstable-nested-components
                optionRender={(option) => (
                    <div className="dropdown-option">
                        {/*
                          The row itself does the selecting. The checkbox is a
                          <label>, so without preventDefault a click on the box
                          also forwards a click to its hidden input — the row
                          would toggle twice and the shift would never select.
                        */}
                        <span role="presentation" onClick={(e) => e.preventDefault()}>
                            <Checkbox checked={selectedShiftKeys.includes(String(option.value))} />
                        </span>
                        <span>{option.label}</span>
                    </div>
                )}
                // Select All sits above the list, outside the virtualised options,
                // so the whole row stays clickable and search never hides it.
                // eslint-disable-next-line react/no-unstable-nested-components
                dropdownRender={(menu) => (
                    <>
                        {/*
                          The row carries the handler, not the checkbox: a global
                          rule (Elements/Dropdown) sets pointer-events: none on
                          every .dropdown-option checkbox, so a checkbox onChange
                          here would never fire.
                        */}
                        <div
                            className="dropdown-option select-all-option"
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSelectAllShifts(!isAllSelected)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    handleSelectAllShifts(!isAllSelected);
                                }
                            }}>
                            <span role="presentation" onClick={(e) => e.preventDefault()}>
                                <Checkbox indeterminate={isIndeterminate} checked={isAllSelected} />
                            </span>
                            <span className="select-all-text">Select All Shifts</span>
                        </div>
                        {menu}
                    </>
                )}
                onDropdownVisibleChange={(open) => setShiftDropdownOpen(open)}
                suffixIcon={shiftDropdownOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                listHeight={400}
                maxTagCount="responsive" />
        </div>
    );

    const renderSecurityDropdown = () => (
        <div className="security-dropdown">
            <Select
                mode="multiple"
                showSearch
                placeholder="Search"
                // Cap at the request's officer count so a single-officer request
                // can't be assigned multiple officers (a free-text name uses a slot).
                maxCount={dropdownOfficerLimit}
                style={{ width: '100%', minHeight: '50px', fontSize: '18px' }}
                onChange={handleSelectSecurity}
                options={securityTeam?.map((item) => ({ value: item.id, label: item.name })) || []}
                filterOption={(input, option) => {
                    if (option?.label === undefined) {
                        return false;
                    }
                    return option?.label?.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                value={selectedSecurityIds}
                suffixIcon={<SearchIcon />} />
        </div>
    );

    // Retail names its officer by hand, so the count wording (which describes the
    // dropdown's slots) would be misleading there.
    const nonRetailOfficerHint = maxOfficers === 1
        ? 'Assign one officer to the selected shift(s).'
        : `Assign up to ${maxOfficers} officers to the selected shift(s) (each officer is one line).`;
    const officerHint = isRetail
        ? 'Enter the officer name for the selected shift(s).'
        : nonRetailOfficerHint;

    return (
        <Modal
            footer={null}
            width={650}
            open={isModalOpen}
            centered
            onCancel={handleCancel}
            title="Assign Security Officer"
            wrapClassName="schedule-action-modal"
            {...rest}>
            <div className="schedule-action-modal__inner">
                {(requestId || siteName) && (
                    <p className="schedule-action-modal__request">
                        {[requestId ? `Request #${requestId}` : null, siteName].filter(Boolean).join(' · ')}
                    </p>
                )}
                <p className="bold">Select Shifts</p>
                { renderShiftDropdown() }
                <p>{officerHint}</p>
                { !isRetail && renderSecurityDropdown() }
                <div className="security-input">
                    <TextInput
                        value={tempSecurityName}
                        placeholder="Input Name"
                        // No free-text officer once the officer slots are full.
                        disabled={selectedSecurityIds.length >= maxOfficers}
                        onChange={handleTempSecurityChange} />
                </div>

                <div className="modal-footer">
                    <Button
                        type="primary"
                        className="form-button"
                        loading={loading}
                        onClick={() => handleSubmit()}>
                        Assign Security Officer
                    </Button>
                    <Button
                        type="primary"
                        ghost
                        className="form-button cancel-button"
                        onClick={handleCancel}>
                        Cancel
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ScheduleModal;
